import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, X } from 'lucide-react'
import { Button, EmptyState } from '@/components/ui'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type { ProjectStatus } from '../api/types'
import { ProjectStatusChip } from './ProjectStatusChip'

/**
 * Modal picker for "Link existing formulation" on the project detail
 * pane (Sprint 10 Chunk 4).
 *
 * Uses the existing `GET /api/formulations?q=` endpoint — same shape
 * the Formulations module list view consumes. Filters out formulations
 * already attached to this project (or to *any* project — the link
 * action on the backend re-points project_id, so we surface a clear
 * "currently in project N" hint when applicable).
 *
 * Debounce 200ms on the search input; query fires when 1+ chars typed.
 *
 * Status enum is reused from this module's own `ProjectStatus` type;
 * Sprint 10 D-2 locked Project + Formulation status to the same
 * 4-value union, so we don't cross the module boundary.
 */

interface FormulationListRow {
  id: number
  formula_code: string
  name: string
  status: ProjectStatus
  customer_id: number | null
  /** Set when the formulation is already linked to a project. The
   *  link endpoint will re-point to the new project, so we warn. */
  project_id?: number | null
  deleted_at: string | null
  updated_at: string | null
}

interface FormulationsResponse {
  formulations: FormulationListRow[]
}

export interface FormulationPickerProps {
  open: boolean
  /** The current project's id — used to filter out already-linked rows
   *  client-side. */
  currentProjectId: number
  /** ids of formulations already in this project; hidden from results. */
  excludeIds: ReadonlySet<number>
  onPick: (formulationId: number) => void
  onCancel: () => void
  pending?: boolean
}

export function FormulationPicker({
  open,
  excludeIds,
  onPick,
  onCancel,
  pending,
}: FormulationPickerProps) {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), 200)
    return () => clearTimeout(id)
  }, [query])

  // Reset the search state when the dialog opens fresh.
  useEffect(() => {
    if (!open) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setQuery('')
     
    setDebounced('')
  }, [open])

  const { data, isFetching } = useQuery({
    queryKey: ['projects', 'formulation-picker', debounced],
    queryFn: () =>
      jsonFetch<FormulationsResponse>(
        debounced
          ? `/api/formulations?q=${encodeURIComponent(debounced)}`
          : '/api/formulations',
      ),
    enabled: open,
    staleTime: 15_000,
  })

  const rows = useMemo<FormulationListRow[]>(() => {
    const all = data?.formulations ?? []
    return all
      .filter((f) => !excludeIds.has(f.id))
      .filter((f) => !f.deleted_at) // hide archived
  }, [data, excludeIds])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="formulation-picker-title"
      className="fixed inset-0 z-modal flex items-center justify-center bg-overlay"
      onClick={onCancel}
    >
      <div
        className="flex h-[70vh] w-full max-w-xl flex-col overflow-hidden rounded-md border border-border-subtle bg-surface shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-2 border-b border-border-subtle bg-surface-sunk px-4 py-2.5">
          <h2 id="formulation-picker-title" className="text-role-data-primary">
            Link existing formulation
          </h2>
          <button
            type="button"
            aria-label="Cancel"
            onClick={onCancel}
            disabled={pending}
            className="cursor-pointer rounded-xs border-none bg-transparent p-0.5 text-tertiary hover:text-primary disabled:opacity-50"
          >
            <X size={14} />
          </button>
        </header>

        <div className="flex items-center gap-2 border-b border-border-subtle bg-surface px-4 py-2">
          <Search size={14} className="text-tertiary" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            placeholder="Search by formula code or name…"
            className="flex-1 border-none bg-transparent text-sm outline-none placeholder:text-tertiary"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {rows.length === 0 ? (
            <div className="p-4">
              <EmptyState
                variant={debounced ? 'no-match' : 'no-data'}
                title={
                  debounced
                    ? 'No formulations match'
                    : isFetching
                      ? 'Loading formulations…'
                      : 'No formulations available'
                }
                description={
                  debounced
                    ? 'Try a different search; archived formulations are hidden.'
                    : 'Create a formulation in the Formulations module first, then come back here to link it.'
                }
              />
            </div>
          ) : (
            <ul className="divide-y divide-border-subtle">
              {rows.map((f) => (
                <li
                  key={f.id}
                  className="flex items-center gap-3 px-4 py-2 hover:bg-surface-hover"
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="text-role-data-primary">
                        {f.formula_code}
                      </span>
                      <ProjectStatusChip status={f.status} />
                      {f.project_id ? (
                        <span className="text-role-metadata text-warning-text">
                          (in another project)
                        </span>
                      ) : null}
                    </div>
                    <span className="truncate text-role-data-secondary">
                      {f.name}
                    </span>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => onPick(f.id)}
                    disabled={pending}
                  >
                    Link
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border-subtle bg-surface-sunk px-4 py-2">
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
        </footer>
      </div>
    </div>
  )
}
